import Template from "./template";

type Options = {
    formSelector: string;
    contentSelector: string;
    templateSelector: string;
    itemSelector: string;
    classNameHidden?: string;
}

const defaultOptions: Options = {
    formSelector: null,
    contentSelector: null,
    templateSelector: null,
    itemSelector: null,
    classNameHidden: 'is-hidden',
}

class Recommendation {
    // @ts-ignore
    constructor(private readonly el: HTMLElement, private readonly form: VariationForm, private options: Options = defaultOptions) {
        this.el = el;
        this.options = {
            ...defaultOptions,
            ...options,
        };

        this.init();
    }

    public init(): void {
        // @ts-ignore
        if (typeof jQuery === 'undefined') {
            return;
        }

        // @ts-ignore
        if (typeof wc_add_to_cart_variation_params === 'undefined') {
            return;
        }

        const recommend = JSON.parse(this.el.dataset.recommend);
        if (!recommend || typeof recommend !== 'object' || recommend.length === 0) {
            return;
        }

        this.form.$form.on('reset_data', this.onReset.bind(this));
        this.form.$form.on('found_variation', this.onFound.bind(this));
        this.el.addEventListener('click', this.onSelect.bind(this));

        this.form.$form.get(0).dispatchEvent(new Event('reset_data'));
    }

    public async setRecommendations(): Promise<void> {
        const data = this.form.useAjax ? await this.getDataRemote() : this.getDataLocal();

        let content: string = '';
        for (const variation of data) {
            content += (new Template(this.el, {
                templateSelector: this.options.templateSelector,
            })).render(variation);
        }

        this.append(content);
    }

    public append(data: string): void {
        (this.el.querySelector(this.options.contentSelector) as HTMLElement).innerHTML = data;
    }

    public clear(): void {
        (this.el.querySelector(this.options.contentSelector) as HTMLElement).innerHTML = '';
    }

    public async load(): Promise<void> {
        this.clear();
        this.visibility();

        if (!this.check()) {
            return;
        }

        await this.setRecommendations();
    }

    public reset(): boolean {
        const required = JSON.parse(this.el.dataset.required);
        if (!required || typeof required !== 'object' || required.length === 0) {
            return false;
        }

        let missing = false;
        const chosen = this.form.getChosenAttributes();
        for (const attribute of required) {
            if (chosen.data[attribute]) {
                continue;
            }

            missing = true;
        }

        if (!missing) {
            return false;
        }

        for (const attribute of JSON.parse(this.el.dataset.recommend)) {
            this.change(attribute, '');
        }

        this.form.$form.trigger('update_variation_values');

        return true;
    }

    public check(): boolean {
        const required = JSON.parse(this.el.dataset.required);
        if (!required || typeof required !== 'object' || required.length === 0) {
            return true;
        }

        const chosen = this.form.getChosenAttributes();
        for (const attribute of required) {
            if (!chosen.data[attribute]) {
                return false;
            }
        }

        return true;
    }

    public visibility(): void {
        const attributes = JSON.parse(this.el.dataset.recommend);
        if (!attributes || typeof attributes !== 'object') {
            return;
        }

        const chosen = this.form.getChosenAttributes();
        const hasRequired = this.check();

        let showNext = 1;
        for (const attribute of attributes) {
            if (!hasRequired) {
                this.hideAttribute(attribute);

                continue;
            }

            if (chosen.data[attribute]) {
                this.showAttribute(attribute);

                continue;
            }

            if (0 < showNext) {
                showNext--;

                this.showAttribute(attribute);

                continue;
            }

            this.hideAttribute(attribute);
        }

        return;
    }

    public showAttribute( attribute: string) : void {
        this.form.$form.get(0).querySelectorAll('[name="' + attribute + '"], [data-attribute_name="' + attribute + '"]').forEach((el: HTMLElement) => {
            el.closest('tr').classList.remove(this.options.classNameHidden);
        });
    }

    public hideAttribute( attribute: string) : void {
        this.form.$form.get(0).querySelectorAll('[name="' + attribute + '"], [data-attribute_name="' + attribute + '"]').forEach((el: HTMLElement) => {
            el.closest('tr').classList.add(this.options.classNameHidden);
        });
    }

    public change(key: string, value: string): void {
        const inputs = this.form.$form.get(0).querySelectorAll('[name="' + key + '"], [data-attribute_name="' + key + '"]');
        if (!inputs) {
            return;
        }

        inputs.forEach((el: HTMLFormElement) => {
            el.value = value;

            el.dispatchEvent(new Event('change'));
        });
    }

    public setForm(data: any): void {
        this.change('variation_id', data.variation_id ?? '');

        for (const [key, value] of Object.entries(data.attributes ?? {})) {
            this.change(key, String(value));
        }

        this.visibility();

        this.form.$form.trigger('update_variation_values');
    }

    public async getDataRemote(): Promise<object[]> {
        const chosen = this.form.getChosenAttributes();
        const body = new URLSearchParams(chosen.data);
        body.set('product_id', String(parseInt(this.form.$form.data('product_id'), 10)));
        body.set('custom_data', this.form.$form.data('custom_data'));

        // @ts-ignore
        const response: Response = await fetch(wc_add_to_cart_variation_params.wc_ajax_url.toString().replace('%%endpoint%%', 'get_variations'), {
            method: 'POST',
            body: body,
        }).catch(error => {
            return [];
        });

        return await response.json();
    }

    public getDataLocal(): object[] {
        const chosen = this.form.getChosenAttributes();

        let data: object[] = [];
        variation: for (const variation of this.form.$form.data('product_variations')) {
            for (const [key, value] of Object.entries(variation.attributes)) {
                if ('' === value) {
                    continue;
                }

                if (!chosen.data[key]) {
                    continue;
                }

                if ('' === chosen.data[key]) {
                    continue;
                }

                if (chosen.data[key] !== value) {
                    continue variation;
                }
            }

            data.push(variation);
        }

        return data;
    }

    private async onReset(event: Event): Promise<void> {
        this.reset();

        await this.load();
    }

    private async onFound(event: Event): Promise<void> {
        await this.setRecommendations();
    }

    private async onSelect(event: Event): Promise<void> {
        const item = (event.target as HTMLElement).closest(this.options.itemSelector);
        if (!item) {
            return;
        }

        const variation = JSON.parse((item as HTMLElement).dataset.variation);
        if (!variation) {
            return;
        }


        this.setForm(variation);
        this.form.$form.trigger('found_variation', [variation]);

        await this.load();

        this.form.$form.trigger('woocommerce_variation_has_changed');
    }
}

export default Recommendation;
